import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { MenuService } from "../../../services/menu.service";
import { BillingCloseSearchItem } from "../../../model/billing/billing-close-search-item";
import {
  SearchFilter,
  IFilterResponse,
} from "../../../infrastructure/ifilter-component";
import { SelectionModel } from "@angular/cdk/collections";
import { BillingCloseService } from "../../../services/billing/billing-close.service";
import { BillingState } from "../../../enums/billing-state.enum";
import { PagerControlComponent } from "../../controls/pager/pager-control.component";
import { SearchFilterComponent } from "../../search-filter/search-filter.component";
import { NotificationToastService } from "../../../services/notification-toast.service";
import {
  NoBillingReasonDialogComponent,
  NoBillingReasonDialogResponse,
} from "../../dialogs/no-billing-reason-dialog/no-billing-reason-dialog.component";
import {
  BillingCloseTaskDialogComponent,
  BillingCloseTaskDialogResponse,
} from "../../dialogs/billing-close-task-dialog/billing-close-task-dialog.component";
import { Router } from "@angular/router";
import { ScheduledTask } from "../../../enums/escheduled-task.enum";
import { CsvExportService } from "../../../services/export/csv-export.service";
import { BillingCloseSearchItemExport } from "src/app/model/billing/billing-close-search-item-export";
import { LogViewDialogComponent } from "../../dialogs/log-view/log-view-dialog.component";
import { BillingLogViewDialogComponent } from "../../dialogs/billing-log-view-dialog/billing-log-view-dialog.component";
import { AuthService } from "../../../services/auth.service";
import { StudyReportService } from "../../../services/study-report/study-report.service";
import { PdfService } from "../../../services/pdf.service";
import { BillingCloseFilterComponent } from "../billing-close-filter/billing-close-filter.component";
import { MedicalAppointmenObservationDialogComponent } from "../../dialogs/medical-appointment-observation/medical-appointment-observation-dialog.component";

@Component({
  selector: "app-billing-close-search",
  templateUrl: "./billing-close-search.component.html",
  styleUrls: ["./billing-close-search.component.css"],
})
export class BillingCloseSearchComponent implements OnInit, OnDestroy {
  private readonly sessionColumnsKey = "billing-close-search.columns.v5";
  private readonly sessionSortKey = "billing-close-search.sort";
  private readonly sessionColumnWidthsKey = "billing-close-search.column-widths.v5";
  private readonly loggedSectorId = this.authService.getSectorId();
  private hasStoredColumnsState = false;

  displayedColumns: string[] = [
    "select",
    "protocol",
    "billingDate",
    "billingStateName",
    "entityName",
    "clientName",
    "clientCode",
    "planName",
    "aliquot",
    "patient",
    "practiceName",
    "serviceName",
    "amount",
    "entityAmount",
    "hasNoBillingObservation",
    "noBillingName",
    "star",
  ];



  seeTotals: boolean = this.authService.seeCloseWithTotals();
  loading: boolean;
  loadingAction: boolean;
  draggedColumn: string | null = null;
  sortActive: string = "";
  sortDirection: string = "";
  allItems: BillingCloseSearchItem[] = [];
  columnFilters: { [key: string]: string } = {};
  columnWidths: { [key: string]: number } = {};
  dataSource: BillingCloseSearchItem[] = [];
  readonly columnsDefinition: Array<{ field: string; title: string }> = [
    { field: "protocol", title: "Protocolo" },
    { field: "billingDate", title: "Fecha" },
    { field: "billingStateName", title: "Estado" },
    { field: "entityName", title: "Entidad" },
    { field: "clientName", title: "Cliente" },
    { field: "clientCode", title: "Código cliente" },
    { field: "planName", title: "Plan" },
    { field: "aliquot", title: "IVA" },
    { field: "patient", title: "Paciente" },
    { field: "practiceName", title: "Práctica" },
    { field: "serviceName", title: "Servicio" },
    { field: "amount", title: "Cantidad" },
    { field: "entityAmount", title: "Total Entidad" },
    { field: "hasNoBillingObservation", title: "Obs. No Fact." },
    { field: "noBillingName", title: "Tipo NO Facturar" },
  ];
  searchFilters: SearchFilter[];
  selection = new SelectionModel<BillingCloseSearchItem>(true, []);
  @ViewChild("pager", { static: true }) pager: PagerControlComponent;
  @ViewChild("searchFilter", { static: false })
  searchFilter: SearchFilterComponent;
  @ViewChild("billingCloseFilter", { static: false })
  billingCloseFilter: BillingCloseFilterComponent;

  constructor(
    private menuService: MenuService,
    private authService: AuthService,
    private notificationToastService: NotificationToastService,
    private dialog: MatDialog,
    private router: Router,
    private studyReportService: StudyReportService,
    private pdfService: PdfService,
    private csvExportService: CsvExportService,
    private billingCloseService: BillingCloseService,
  ) {
    this.menuService.setMenuTitle("Facturación", "Prefacturación");
  }

  ngOnDestroy() {
    this.removeResizeListeners();
  }

  async copyFilter() {
    await this.billingCloseFilter.saveFiltersOnServer();
  }

  ngOnInit() {
    if (!this.seeTotals) {
      this.displayedColumns = this.displayedColumns.filter(
        (col) => col !== "entityAmount",
      );
    }

    this.restoreColumnsOrder();
    this.normalizeDisplayedColumns();
    this.restoreSortState();
    this.restoreColumnWidths();
  }

  get selected() {
    return this.selection.selected.length;
  }

  get info(): string {
    if (!this.seeTotals) {
      return null;
    }

    const totalItems = this.pager.pager.totalItems;
    const totalSelected = this.selection.selected.length;

    let totalAmount = 0;
    this.selection.selected.forEach((item) => {
      totalAmount += item.entityAmount ? item.entityAmount : 0;
    });

    return `Total: ${totalAmount.toFixed(2)}`;
  }

  isAllSelected() {
    if (this.selection.selected && this.pager.getAllItems()) {
      const numSelected = this.selection.selected.length;
      const numRows = this.pager.getAllItems().length;
      return numSelected === numRows;
    } else {
      return false;
    }
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.pager.getAllItems().forEach((row) => this.selection.select(row));
  }

  isSectorMismatch(item: BillingCloseSearchItem): boolean {
    if (!item || item.sectorId == null || !this.loggedSectorId) {
      return false;
    }

    return item.sectorId !== this.loggedSectorId;
  }

  onStartSearch(): void {}

  selectedPageIndexChange(pagedItems: any[]) {
    this.dataSource = pagedItems ? [...pagedItems] : [];
  }

  get exportColumns(): string[] {
    return this.orderedColumnsDefinition.map((col) => col.field);
  }

  get exportColumnsName(): string[] {
    return this.orderedColumnsDefinition.map((col) => col.title);
  }

  get availableColumns(): Array<{ field: string; title: string }> {
    if (!this.seeTotals) {
      return this.columnsDefinition.filter(
        (column) => column.field !== "entityAmount",
      );
    }

    return this.columnsDefinition;
  }

  get orderedColumnsDefinition(): Array<{ field: string; title: string }> {
    const order = this.displayedColumns.filter(
      (column) => column !== "select" && column !== "star",
    );
    const visibleColumns = new Set(order);
    const orderMap = new Map<string, number>(
      order.map((column, index) => [column, index]),
    );

    return this.columnsDefinition
      .filter((column) => visibleColumns.has(column.field))
      .sort((a, b) => {
      const aIndex = orderMap.has(a.field) ? orderMap.get(a.field) : 999;
      const bIndex = orderMap.has(b.field) ? orderMap.get(b.field) : 999;
      return aIndex - bIndex;
      });
  }

  isColumnVisible(column: string): boolean {
    return this.displayedColumns.indexOf(column) >= 0;
  }

  setColumnVisibility(column: string, visible: boolean) {
    const dataColumns = this.displayedColumns.filter(
      (item) => item !== "select" && item !== "star",
    );

    if (visible) {
      if (dataColumns.indexOf(column) < 0) {
        dataColumns.push(column);
      }
    } else {
      if (dataColumns.length <= 1) {
        return;
      }

      const removeIndex = dataColumns.indexOf(column);
      if (removeIndex >= 0) {
        dataColumns.splice(removeIndex, 1);
      }

      if (this.sortActive === column) {
        this.sortActive = "";
        this.sortDirection = "";
        sessionStorage.removeItem(this.sessionSortKey);
      }

      if (this.columnFilters[column]) {
        delete this.columnFilters[column];
      }
    }

    this.displayedColumns = ["select", ...dataColumns, "star"];
    this.saveColumnsOrder();
    this.refreshGrid();
  }

  showAllColumns() {
    const allColumns = this.availableColumns.map((column) => column.field);
    this.displayedColumns = ["select", ...allColumns, "star"];
    this.saveColumnsOrder();
    this.refreshGrid();
  }

  sortByColumn(column: string) {
    if (this.sortActive !== column) {
      this.sortAllData({ active: column, direction: "asc" });
      return;
    }

    if (this.sortDirection === "asc") {
      this.sortAllData({ active: column, direction: "desc" });
      return;
    }

    this.sortAllData({ active: "", direction: "" });
  }

  getSortIcon(column: string): string {
    if (this.sortActive !== column) {
      return "unfold_more";
    }

    return this.sortDirection === "asc" ? "arrow_upward" : "arrow_downward";
  }

  sortAllData(sortEvent: any) {
    if (!sortEvent || !sortEvent.active || sortEvent.direction === "") {
      this.sortActive = "";
      this.sortDirection = "";
      sessionStorage.removeItem(this.sessionSortKey);
      this.refreshGrid();
      return;
    }

    this.sortActive = sortEvent.active;
    this.sortDirection = sortEvent.direction;
    this.saveSortState();
    this.refreshGrid();
  }

  onColumnFilterInput(column: string, value: string) {
    this.columnFilters[column] = value;
    this.refreshGrid();
  }

  getColumnWidth(column: string): number | null {
    const width = this.columnWidths[column];
    return width && width > 0 ? width : null;
  }

  startResize(event: MouseEvent, column: string) {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const headerCell = (event.target as HTMLElement).closest("th");
    const measuredWidth = headerCell
      ? Math.round(headerCell.getBoundingClientRect().width)
      : 100;
    const startWidth = this.getColumnWidth(column) || Math.max(80, measuredWidth);

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidth = Math.max(80, startWidth + delta);
      this.columnWidths = {
        ...this.columnWidths,
        [column]: nextWidth,
      };
    };

    const onMouseUp = () => {
      this.removeResizeListeners();
      this.saveColumnWidths();
    };

    (window as any).__billingCloseResizeMove = onMouseMove;
    (window as any).__billingCloseResizeUp = onMouseUp;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  onHeaderDragStart(event: DragEvent, column: string) {
    this.draggedColumn = column;

    if (event.dataTransfer) {
      event.dataTransfer.setData("text/plain", column);
      event.dataTransfer.effectAllowed = "move";
    }
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  onHeaderDrop(event: DragEvent, targetColumn: string) {
    event.preventDefault();

    if (!this.draggedColumn || this.draggedColumn === targetColumn) {
      return;
    }

    const dataColumns = this.displayedColumns.filter(
      (column) => column !== "select" && column !== "star",
    );
    const sourceIndex = dataColumns.indexOf(this.draggedColumn);
    const targetIndex = dataColumns.indexOf(targetColumn);

    if (sourceIndex < 0 || targetIndex < 0) {
      this.draggedColumn = null;
      return;
    }

    moveItemInArray(dataColumns, sourceIndex, targetIndex);
    this.displayedColumns = ["select", ...dataColumns, "star"];
    this.saveColumnsOrder();
    this.draggedColumn = null;
  }

  onHeaderDragEnd() {
    this.draggedColumn = null;
  }

  private getSortableValue(item: BillingCloseSearchItem, property: string): any {
    if (property === "billingDate") {
      return item.billingDate ? new Date(item.billingDate).getTime() : 0;
    }

    if (property === "practiceName") {
      return item.setName
        ? item.setName.toLocaleLowerCase()
        : (item.practiceName ? item.practiceName.toLocaleLowerCase() : "");
    }

    if (property === "clientName") {
      return item.clientName ? item.clientName.toLocaleLowerCase() : "";
    }

    if (property === "serviceName") {
      return item.serviceName ? item.serviceName.toLocaleLowerCase() : "";
    }

    if (property === "noBillingName") {
      return item.noBillingName ? item.noBillingName.toLocaleLowerCase() : "";
    }

    if (property === "aliquot") {
      return item.aliquot != null ? item.aliquot : 0;
    }

    const value = item[property];
    if (value === null || value === undefined) {
      return "";
    }

    return typeof value === "string" ? value.toLocaleLowerCase() : value;
  }

  async onUserFilterLoad() {
    await this.searchFilter.search();
  }

  onEndSearch(response: IFilterResponse<BillingCloseSearchItem>): void {

    this.searchFilters = response &&  response.searchFilters ? response.searchFilters : [];
    this.selection = new SelectionModel<BillingCloseSearchItem>(true, []);
    this.allItems = response && response.items ? [...response.items] : [];
    this.refreshGrid();
  }

  private saveColumnsOrder() {
    sessionStorage.setItem(this.sessionColumnsKey, JSON.stringify(this.displayedColumns));
  }

  private restoreColumnsOrder() {
    const stored = sessionStorage.getItem(this.sessionColumnsKey);
    if (!stored) {
      return;
    }

    try {
      const parsed: string[] = JSON.parse(stored);
      if (!parsed || !Array.isArray(parsed)) {
        return;
      }

      const allowed = new Set(this.availableColumns.map((column) => column.field));
      const parsedDataColumns = parsed
        .filter((column) => column !== "select" && column !== "star")
        .filter((column, index, arr) => arr.indexOf(column) === index)
        .filter((column) => allowed.has(column));

      if (parsedDataColumns.length > 0) {
        this.displayedColumns = ["select", ...parsedDataColumns, "star"];
        this.hasStoredColumnsState = true;
      }
    } catch {
      sessionStorage.removeItem(this.sessionColumnsKey);
    }
  }

  private normalizeDisplayedColumns() {
    const expectedDataColumns = this.availableColumns.map((column) => column.field);
    const expectedSet = new Set(expectedDataColumns);
    const currentDataColumns = this.displayedColumns
      .filter((column) => column !== "select" && column !== "star")
      .filter((column) => expectedSet.has(column));

    let normalizedDataColumns = [...currentDataColumns];

    if (!this.hasStoredColumnsState) {
      normalizedDataColumns = [...expectedDataColumns];
    }

    if (normalizedDataColumns.length === 0) {
      normalizedDataColumns = [...expectedDataColumns];
    }

    this.displayedColumns = ["select", ...normalizedDataColumns, "star"];
  }

  private saveSortState() {
    sessionStorage.setItem(
      this.sessionSortKey,
      JSON.stringify({ active: this.sortActive, direction: this.sortDirection }),
    );
  }

  private saveColumnWidths() {
    sessionStorage.setItem(
      this.sessionColumnWidthsKey,
      JSON.stringify(this.columnWidths),
    );
  }

  private restoreColumnWidths() {
    const stored = sessionStorage.getItem(this.sessionColumnWidthsKey);
    if (!stored) {
      this.columnWidths = {};
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== "object") {
        return;
      }

      const validColumns = new Set([
        "protocol",
        "billingDate",
        "billingStateName",
        "entityName",
        "clientName",
        "clientCode",
        "planName",
        "aliquot",
        "patient",
        "practiceName",
        "serviceName",
        "amount",
        "entityAmount",
        "hasNoBillingObservation",
        "noBillingName",
      ]);

      const restored: { [key: string]: number } = {};
      Object.keys(parsed).forEach((column) => {
        if (!validColumns.has(column)) {
          return;
        }

        const width = Number(parsed[column]);
        if (!isNaN(width) && width >= 80 && width <= 500) {
          restored[column] = width;
        }
      });

      this.columnWidths = restored;
    } catch {
      sessionStorage.removeItem(this.sessionColumnWidthsKey);
      this.columnWidths = {};
    }
  }

  private restoreSortState() {
    const stored = sessionStorage.getItem(this.sessionSortKey);
    if (!stored) {
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      if (parsed && parsed.active && parsed.direction) {
        this.sortActive = parsed.active;
        this.sortDirection = parsed.direction;
      }
    } catch {
      sessionStorage.removeItem(this.sessionSortKey);
    }
  }

  private sortItems(
    items: BillingCloseSearchItem[],
    active: string,
    direction: string,
  ): BillingCloseSearchItem[] {
    return items.sort((a, b) => {
      const aValue = this.getSortableValue(a, active);
      const bValue = this.getSortableValue(b, active);

      if (aValue < bValue) {
        return direction === "asc" ? -1 : 1;
      }

      if (aValue > bValue) {
        return direction === "asc" ? 1 : -1;
      }

      return 0;
    });
  }

  private refreshGrid() {
    const currentPage = this.pager && this.pager.pager && this.pager.pager.currentPage
      ? this.pager.pager.currentPage
      : 1;

    let items = this.applyColumnFilters(this.allItems || []);

    if (this.sortActive && this.sortDirection) {
      items = this.sortItems([...items], this.sortActive, this.sortDirection);
    }

    this.pager.setItems(items);
    if (currentPage > 1) {
      this.pager.setPage(currentPage);
    }
  }

  private applyColumnFilters(items: BillingCloseSearchItem[]): BillingCloseSearchItem[] {
    const filters = Object.keys(this.columnFilters || {})
      .filter((key) => !!this.columnFilters[key] && this.columnFilters[key].trim() !== "");

    if (filters.length === 0) {
      return [...items];
    }

    return items.filter((item) => {
      return filters.every((column) => {
        const filterValue = this.normalizeFilterValue(this.columnFilters[column]);
        const itemValue = this.normalizeFilterValue(this.getColumnFilterValue(item, column));
        return itemValue.indexOf(filterValue) >= 0;
      });
    });
  }

  private getColumnFilterValue(item: BillingCloseSearchItem, column: string): string {
    if (!item) {
      return "";
    }

    switch (column) {
      case "protocol":
        return item.protocol != null ? String(item.protocol) : "";
      case "billingDate": {
        if (!item.billingDate) {
          return "";
        }

        const date = new Date(item.billingDate);
        const day = (`0${date.getDate()}`).slice(-2);
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const year = date.getFullYear();
        const hours = (`0${date.getHours()}`).slice(-2);
        const minutes = (`0${date.getMinutes()}`).slice(-2);
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      }
      case "practiceName":
        return item.setName ? item.setName : (item.practiceName ? item.practiceName : "");
      case "hasNoBillingObservation":
        return item.hasNoBillingObservation ? "si" : "no";
      case "clientName":
        return item.clientName != null ? String(item.clientName) : "";
      case "aliquot":
        return item.aliquot != null ? String(item.aliquot) : "";
      case "serviceName":
        return item.serviceName != null ? String(item.serviceName) : "";
      case "noBillingName":
        return item.noBillingName != null ? String(item.noBillingName) : "";
      default: {
        const value = item[column];
        return value != null ? String(value) : "";
      }
    }
  }

  private normalizeFilterValue(value: string): string {
    const normalized = value ? String(value) : "";
    return normalized
      .toLocaleLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  hasNoBillingObservationIndicator(item: BillingCloseSearchItem): boolean {
    return !!(
      (item && item.hasNoBillingObservation) ||
      (item && item.noBillingObservation && item.noBillingObservation.trim().length > 0)
    );
  }

  private removeResizeListeners() {
    const onMouseMove = (window as any).__billingCloseResizeMove;
    const onMouseUp = (window as any).__billingCloseResizeUp;

    if (onMouseMove) {
      document.removeEventListener("mousemove", onMouseMove);
      (window as any).__billingCloseResizeMove = null;
    }

    if (onMouseUp) {
      document.removeEventListener("mouseup", onMouseUp);
      (window as any).__billingCloseResizeUp = null;
    }
  }

  async changeStatePartial() {
    if (this.selection.selected.length > 0) {
      this.changeState(BillingState.partial);
    }
  }

  async changeStateControl() {
    if (this.selection.selected.length > 0) {
      this.changeState(BillingState.control);
    }
  }

  viewOrdenControl(billingCloseSearchItem: BillingCloseSearchItem) {
    this.router.navigate([
      "billing-orden-control-wizard",
      billingCloseSearchItem.medicalAppointmentId,
      billingCloseSearchItem.protocol,
      billingCloseSearchItem.medicalOrderId,
    ]);
  }

  openMedicalAppointmentObservationDialog(
    billingCloseSearchItem: BillingCloseSearchItem,
  ): void {
    const dialogRef = this.dialog.open(
      MedicalAppointmenObservationDialogComponent,
      {
        width: "50%",
        data: {
          medicalAppointmentId: billingCloseSearchItem.medicalAppointmentId,
          editLastObservation: true,
        },
      },
    );
  }

  exportStudyReport() {
    if (this.selection.selected.length > 0) {
      this.dialog.open(BillingCloseTaskDialogComponent, {
        width: "50%",
        data: {
          escheduledTaskId: ScheduledTask.billingExportStudyReport,
          taskName: `${this.authService
            .getUserName()
            .toUpperCase()} - EXPORTACION-INFORMES`,
          medicalAppointmentProcedureIds: this.selection.selected.map(
            (s) => s.medicalAppointmentProcedureId,
          ),
        },
      });
    }
  }

  async massivePrint() {
    try {
      if (this.selection.selected.length > 0) {
        this.loadingAction = true;
        let studyReportIds = this.selection.selected.map(
          (s) => s.studyReportId,
        );
        const reports =
          await this.studyReportService.getStudyReportIdStudiesReportPdf(
            studyReportIds,
            false,
          );
        const items = this.pager.getAllItems();

        if (reports && reports.length > 0) {
          for (let index = 0; index < items.length; index++) {
            if (items[index].studyReportId != null) {
              const sItem = this.selection.selected.find(
                (r) =>
                  r.medicalAppointmentProcedureId ===
                  items[index].medicalAppointmentProcedureId,
              );
              if (sItem != null) {
                const inx = reports.findIndex(
                  (r) => r.StudyReportId === sItem.studyReportId,
                );
                if (inx >= 0) {
                  if (reports[inx].ReportBase64) {
                    this.pdfService.base64ToPdfPrint(reports[inx].ReportBase64);
                  }
                  reports.slice(inx, 1);
                }
              }
            }
          }
        }
      }
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Informe");
    } finally {
      this.loadingAction = false;
    }
  }

  exportAttached() {
    if (this.selection.selected.length > 0) {
      this.dialog.open(BillingCloseTaskDialogComponent, {
        width: "50%",
        data: {
          escheduledTaskId: ScheduledTask.exportAttached,
          taskName: `${this.authService
            .getUserName()
            .toUpperCase()} - DESCARGA-ADJUNTOS`,
          medicalAppointmentIds: this.selection.selected.map(
            (s) => s.medicalAppointmentId,
          ),
        },
      });
    }
  }

  exportImages() {
    if (this.selection.selected.length > 0) {
      this.dialog.open(BillingCloseTaskDialogComponent, {
        width: "50%",
        data: {
          escheduledTaskId: ScheduledTask.billingExportImages,
          taskName: `${this.authService
            .getUserName()
            .toUpperCase()} - EXPORTACION-IMAGENES`,
          medicalAppointmentProcedureIds: this.selection.selected.map(
            (s) => s.medicalAppointmentProcedureId,
          ),
        },
      });
    }
  }

  validateOnLine() {
    if (this.selection.selected.length > 0) {
      this.dialog.open(BillingCloseTaskDialogComponent, {
        width: "50%",
        data: {
          escheduledTaskId: ScheduledTask.billingValidationOnLine,
          taskName: `${this.authService
            .getUserName()
            .toUpperCase()} - VALIDACION-ONLINE`,
          medicalAppointmentProcedureIds: this.selection.selected.map(
            (s) => s.medicalAppointmentProcedureId,
          ),
        },
      });
    }
  }

  preBilling() {
    if (this.selection.selected.length > 0) {
      const dialogRef = this.dialog.open(BillingCloseTaskDialogComponent, {
        width: "50%",
        data: {
          escheduledTaskId: ScheduledTask.billingPreBilling,
          taskName: `${this.authService
            .getUserName()
            .toUpperCase()} - PREFACTURACION`,
          medicalAppointmentProcedureIds: this.selection.selected.map(
            (s) => s.medicalAppointmentProcedureId,
          ),
        },
      });

      dialogRef
        .afterClosed()
        .subscribe((response: BillingCloseTaskDialogResponse) => {
          if (response.reload) {
            this.searchFilter.search();
          }
        });
    }
  }

  async changeStateNoBilling() {
    if (this.selection.selected.length > 0) {
      const dialogRef = this.dialog.open(NoBillingReasonDialogComponent, {
        width: "50%",
        data: {},
      });

      dialogRef
        .afterClosed()
        .subscribe((response: NoBillingReasonDialogResponse) => {
          if (response) {
            this.changeState(
              BillingState.noBilling,
              response.noBillingReason.Id,
              response.observations,
            );
          }
        });
    }
  }

  async changeStateDown() {
    if (this.selection.selected.length > 0) {
      this.changeState(BillingState.down);
    }
  }

  async rollBack() {
    try {
      if (this.selection.selected.length > 0) {
        this.loadingAction = true;
        const medicalAppointmentProcedureIds = this.selection.selected.map(
          (s) => s.medicalAppointmentProcedureId,
        );
        await this.billingCloseService.billingOrderControlStateRollBack(
          medicalAppointmentProcedureIds,
        );
        this.searchFilter.search();
      }
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Cierre");
    } finally {
      this.loadingAction = false;
    }
  }

  async changeState(
    billingStateId: number,
    noBillingReasonId?: number,
    observations?: string,
  ) {
    try {
      this.loadingAction = true;
      const medicalAppointmentProcedureIds = this.selection.selected.map(
        (s) => s.medicalAppointmentProcedureId,
      );
      await this.billingCloseService.billingOrderControlStateChange(
        medicalAppointmentProcedureIds,
        billingStateId,
        noBillingReasonId,
        observations,
      );
      this.searchFilter.search();
    } catch (err) {
      this.notificationToastService.error(JSON.stringify(err.error), "Cierre");
    } finally {
      this.loadingAction = false;
    }
  }

  exportCsv() {
    if (this.pager.getAllItems()) {
      const items: BillingCloseSearchItemExport[] = [];
      const columns: string[] = [
        "Protocolo",
        "Estado",
        "Fecha",
        "Entidad",
        "Plan",
      ];
      this.pager.getAllItems().forEach((item) => {
        const itemExport = new BillingCloseSearchItemExport();
        itemExport.Protocolo = item.protocol;
        itemExport.Estado = item.billingStateName;
        itemExport.Fecha = item.billingDate;
        itemExport.Entidad = item.entityName;
        itemExport.Plan = item.planName;
        items.push(itemExport);
      });
      this.csvExportService.downloadFile(items, columns, "prefacturacion");
    }
  }

  openBillingLogViewDialog(
    billingCloseSearchItem: BillingCloseSearchItem,
  ): void {
    this.dialog.open(BillingLogViewDialogComponent, {
      width: "50%",
      data: {
        medicalAppointmentId: billingCloseSearchItem.medicalAppointmentId,
        medicalAppointmentProcedureId:
          billingCloseSearchItem.medicalAppointmentProcedureId,
      },
    });
  }

  openAdmisionLogViewDialog(
    billingCloseSearchItem: BillingCloseSearchItem,
  ): void {
    this.dialog.open(LogViewDialogComponent, {
      width: "50%",
      data: billingCloseSearchItem.medicalAppointmentId,
    });
  }
}
